import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';

import Services, { SortingUtil } from '../helpers/sdkServices';

const ProjectEntitiesContext = createContext({});

const ProjectEntitiesProvider = ({ children }) => {
  const projectId = useCurrentProjectId();

  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isUsersError, setIsUsersError] = useState(false);
  const [loadUsersEnabled, setLoadUsersEnabled] = useState(false);
  const [users, setUsers] = useState([]);

  const getProjectUsers = useCallback(async () => {
    setIsUsersLoading(true);
    setIsUsersError(false);

    try {
      const users = await Services.Projects.getUsers(projectId, {
        useCache: true,
        useConstructionAPI: false,
      });
      const sortedUsers = users.sort(SortingUtil.naturalCompare);
      setUsers(sortedUsers);

      return sortedUsers;
    } catch (error) {
      setIsUsersError(true);

      return [];
    } finally {
      setIsUsersLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadUsersEnabled && getProjectUsers();
  }, [loadUsersEnabled, getProjectUsers]);

  return (
    <ProjectEntitiesContext.Provider
      value={{
        users,
        isUsersLoading,
        isUsersError,
        loadUsersEnabled,
        setLoadUsersEnabled,
      }}
    >
      {children}
    </ProjectEntitiesContext.Provider>
  );
};

ProjectEntitiesProvider.propTypes = {
  children: PropTypes.node,
};

const useProjectUsers = () => {
  const { users, isUsersLoading, isUsersError, setLoadUsersEnabled } =
    useContext(ProjectEntitiesContext);

  useEffect(() => {
    setLoadUsersEnabled(true);
  }, [setLoadUsersEnabled]);

  return { data: users, loading: isUsersLoading, error: isUsersError };
};

export { useProjectUsers, ProjectEntitiesProvider as default };
